import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/sharp-solid-svg-icons';
import { imageFileResizer } from '@peacechen/react-image-file-resizer';
import { Feature, GeoJsonProperties, Geometry, Polygon } from 'geojson';
import html2canvas from 'html2canvas';
import { GeoJSONSource, LngLat, PointLike } from 'maplibre-gl';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import Button, { ButtonVariant } from '@/components/button';
import FileHorizontal from '@/components/icons/FileHorizontal';
import FileVertical from '@/components/icons/FileVertical';
import Typography from '@/components/typography';
import {
  antiScreenshotElementProps,
  GLOBAL_TOOLTIP,
} from '@/constants/constants';
import useWindowDimensions from '@/hooks/useWindowDimensions';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { useModalContext } from '@/providers/ModalProvider';
import { useToastContext } from '@/providers/ToastProvider';

import { useNavigationContext } from '../provider';
import AerialMapModal from './aerialMapModal';
import {
  aerialMapboxBackgroundLayer,
  aerialMapboxBackgroundLayerSourceId,
  aerialMapboxLayer,
  aerialMapboxLayerSourceId,
  fullScreenPolygon,
} from './layers/aerialMapLayers';
import OrientationOption from './orientationOption';

export enum Orientation {
  LANDSCAPE = 'landscape',
  PORTRAIT = 'portrait',
}

const aerialMapTooltipContent =
  'Only selections and customizations within the highlighted area will appear in the report. Drag and zoom screen to adjust.';

const aspectRatio = 3 / 4; // Set 3:4 aspect ratio for the box

// These are the actual width and height needed for ellicott
const orientationWidthAndHeight = {
  [Orientation.LANDSCAPE]: {
    height: 480,
    width: 776,
  },
  [Orientation.PORTRAIT]: {
    height: 660,
    width: 596,
  },
};

export const AerialMapToast: FC = () => {
  const { clearToastContent } = useToastContext();
  const { setModalContent, clearModalContent } = useModalContext();
  const { setAerialMapIsOpen, closeAerialMap } = useNavigationContext();
  const windowDimensions = useWindowDimensions();
  const { activeFileId } = useFileManagementContext();

  const { mapInstance: map } = useMapboxContext();

  const [selectedOrientation, setSelectedOrientation] = useState<Orientation>(
    Orientation.LANDSCAPE
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [captureBounds, setCaptureBounds] = useState<LngLat[] | null>(null);

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        closeAerialMap();
      }
    },
    [closeAerialMap]
  );

  // Convert image to file
  const imageFile = useCallback(
    async (dataUrl: string) => {
      const maxWidth = orientationWidthAndHeight[selectedOrientation].width;
      const maxHeight = orientationWidthAndHeight[selectedOrientation].height;

      return fetch(dataUrl)
        .then((res) => res.blob())
        .then(async (blob) => {
          const tempImageAsFile = new File([blob], 'aerial_map.webp', {
            type: 'image/webp',
          });

          const resizeFile = await imageFileResizer({
            compressFormat: 'webp',
            file: tempImageAsFile,
            maxHeight,
            maxWidth,
            minHeight: maxHeight,
            minWidth: maxWidth,
            outputType: 'file',
            quality: 100,
            rotation: 0,
          });

          return resizeFile as File;
        });
    },
    [selectedOrientation]
  );

  const captureImageFromBounds = useCallback(() => {
    setIsLoading(true);
    if (!map || !captureBounds) return;

    const bottomLeft = map.project(captureBounds[0]);
    const topRight = map.project(captureBounds[1]);

    // Wait for the map to render
    map.once('render', async () => {
      const canvas = await html2canvas(document.body);

      // Adjust for device pixel ratio (DPR)
      const pixelRatio = window.devicePixelRatio || 1;

      // Create a new canvas with the size of the rectangle area, adjusted for DPR
      const rectCanvas = document.createElement('canvas');
      const width = (topRight.x - bottomLeft.x) * pixelRatio; // Adjust width for DPR
      const height = (bottomLeft.y - topRight.y) * pixelRatio; // Adjust height for DPR
      rectCanvas.width = width;
      rectCanvas.height = height;

      const rectContext = rectCanvas.getContext('2d');
      if (!rectContext) return;

      // Draw the portion of the map that corresponds to the rectangle
      // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage#examples
      rectContext.drawImage(
        canvas,
        bottomLeft.x * pixelRatio, // Source x, y adjusted for DPR
        topRight.y * pixelRatio,
        width, // Source width, height
        height,
        0, // Destination x, y
        0,
        width, // Destination width, height
        height
      );

      // Convert to data URL
      const dataUrl = rectCanvas.toDataURL('image/webp');

      // resize the image and make into a File
      const imageAsFile = await imageFile(dataUrl);

      // Remove new canvas
      rectCanvas.remove();

      setModalContent({
        content: (
          <AerialMapModal
            activeFileId={activeFileId}
            image={imageAsFile}
            orientation={selectedOrientation}
          />
        ),
        onCloseEvent: clearModalContent,
      });

      clearToastContent();
      setIsLoading(false);
    });
  }, [
    activeFileId,
    captureBounds,
    clearModalContent,
    imageFile,
    map,
    selectedOrientation,
    setModalContent,
    clearToastContent,
  ]);

  // Calculate the scaling factor based on the selected orientation
  // We can fit more content in landscape mode
  const normalizedScallingFacter = useMemo(() => {
    const screenWidth = windowDimensions.windowWidth || window.innerWidth;
    const screenHeight = windowDimensions.windowHeight || window.innerHeight;

    const pixelRatio = window.devicePixelRatio || 1;

    const MIN_SCALE = 0.5;
    const MAX_SCALE = 1.0;

    const normalizedScreenSize = Math.sqrt(screenWidth * screenHeight);

    const baseFactor = normalizedScreenSize / 1000;

    let scalingFactor = baseFactor * pixelRatio;

    if (selectedOrientation === Orientation.LANDSCAPE) {
      scalingFactor *= 0.5;
    } else {
      scalingFactor *= 0.9;
    }
    return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scalingFactor));
  }, [selectedOrientation, windowDimensions]);

  const handleAddingBox = useCallback(() => {
    if (!map) return;

    const canvas = map.getCanvas();
    const canvasWidth = canvas.clientWidth;
    const canvasHeight = canvas.clientHeight;

    // Determine the smaller dimension to fit the rectangle within the map view
    let rectangleWidth: number;
    let rectangleHeight: number;

    // If the height is smaller, calculate based on height
    if (canvasHeight < canvasWidth) {
      rectangleHeight = canvasHeight * 0.3; // 30% of canvas height
      rectangleWidth = rectangleHeight * aspectRatio;
    } else {
      // Otherwise, calculate based on width
      rectangleWidth = canvasWidth * 0.3; // 30% of canvas width
      rectangleHeight = rectangleWidth / aspectRatio;
    }

    // Adjust for landscape orientation
    if (selectedOrientation === Orientation.LANDSCAPE) {
      const temp = rectangleWidth;
      rectangleWidth = rectangleHeight;
      rectangleHeight = temp;
    }

    // Get the center of the map in screen coordinates
    const center = map.getCenter();
    const centerPoint = map.project([center.lng, center.lat]);

    // Ensure the rectangle dimensions are within the map's bounds
    const mapBounds = map.getBounds();
    const maxLng = mapBounds.getEast();
    const minLng = mapBounds.getWest();
    const maxLat = mapBounds.getNorth();
    const minLat = mapBounds.getSouth();

    // Adjust the top-right and bottom-left points to be within bounds
    const newTopRightPoint = {
      x: Math.min(
        centerPoint.x + rectangleWidth / normalizedScallingFacter,
        map.getCanvas().width
      ),
      y: Math.max(
        Math.min(
          centerPoint.y - rectangleHeight / normalizedScallingFacter,
          map.getCanvas().height
        ),
        0
      ),
    } as PointLike;

    const newBottomLeftPoint = {
      x: Math.max(centerPoint.x - rectangleWidth / normalizedScallingFacter, 0),
      y: Math.min(
        centerPoint.y + rectangleHeight / normalizedScallingFacter,
        map.getCanvas().height
      ),
    } as PointLike;

    // Convert the new points back to geographical coordinates
    const newTopRight = map.unproject(newTopRightPoint);
    const newBottomLeft = map.unproject(newBottomLeftPoint);

    // Ensure the geographical coordinates are within the map's bounds
    const boundedTopRight = {
      lat: Math.min(Math.max(newTopRight.lat, minLat), maxLat),
      lng: Math.min(Math.max(newTopRight.lng, minLng), maxLng),
    };

    const boundedBottomLeft = {
      lat: Math.min(Math.max(newBottomLeft.lat, minLat), maxLat),
      lng: Math.min(Math.max(newBottomLeft.lng, minLng), maxLng),
    };

    // Create a polygon with the bounded coordinates
    const boundsPolygon = [
      [boundedTopRight.lng, boundedTopRight.lat],
      [boundedBottomLeft.lng, boundedTopRight.lat],
      [boundedBottomLeft.lng, boundedBottomLeft.lat],
      [boundedTopRight.lng, boundedBottomLeft.lat],
      [boundedTopRight.lng, boundedTopRight.lat],
    ];

    const boundsPolygonLayer = {
      geometry: {
        coordinates: [boundsPolygon],
        type: 'Polygon',
      } as Polygon,
      properties: {},
      type: 'Feature',
    } as Feature<Geometry, GeoJsonProperties>;

    const aerialMapSource = map.getSource(
      aerialMapboxLayerSourceId
    ) as GeoJSONSource;
    if (!aerialMapSource) return;

    aerialMapSource.setData({
      features: [boundsPolygonLayer],
      type: 'FeatureCollection',
    });

    const aerialMapBackgroundSource = map.getSource(
      aerialMapboxBackgroundLayerSourceId
    ) as GeoJSONSource;

    if (!aerialMapBackgroundSource) return;

    const backgroundPolygon = {
      geometry: {
        coordinates: [fullScreenPolygon[0], boundsPolygon], // Polygon w/ hole. The hole being the box we want to get a screenshot of.
        type: 'Polygon',
      } as Polygon,
      properties: {},
      type: 'Feature',
    } as Feature<Geometry, GeoJsonProperties>;

    aerialMapBackgroundSource.setData({
      features: [backgroundPolygon],
      type: 'FeatureCollection',
    });

    setCaptureBounds([newBottomLeft, newTopRight]);
  }, [map, normalizedScallingFacter, selectedOrientation]);

  useEffect(() => {
    if (!map) return () => {};

    setAerialMapIsOpen(true);
    document.addEventListener('keydown', handleKeyPress);

    if (!map.getLayer(aerialMapboxLayer.id)) {
      map.addLayer(aerialMapboxLayer);

      // Add the background layer
      if (!map.getLayer(aerialMapboxBackgroundLayer.id)) {
        map.addLayer(aerialMapboxBackgroundLayer);
      }

      // Init add the box
      handleAddingBox();
    }

    // Move the box when the map moves
    map.on('move', handleAddingBox);

    return () => {
      map.off('move', handleAddingBox);

      if (map.getLayer(aerialMapboxLayer.id)) {
        map.removeLayer(aerialMapboxLayer.id);
      }
      if (map.getSource(aerialMapboxLayerSourceId)) {
        map.removeSource(aerialMapboxLayerSourceId);
      }

      if (map.getLayer(aerialMapboxBackgroundLayer.id)) {
        map.removeLayer(aerialMapboxBackgroundLayer.id);
      }

      if (map.getSource(aerialMapboxBackgroundLayerSourceId)) {
        map.removeSource(aerialMapboxBackgroundLayerSourceId);
      }

      setCaptureBounds(null);

      setAerialMapIsOpen(false);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleAddingBox, handleKeyPress, map, setAerialMapIsOpen, isLoading]);

  return (
    <div
      {...antiScreenshotElementProps}
      className="fixed left-4 top-16 flex w-[175px] flex-col bg-white px-4 py-3"
    >
      <div
        className="flex flex-row pb-4"
        data-tooltip-content={aerialMapTooltipContent}
        data-tooltip-id={GLOBAL_TOOLTIP}
        data-tooltip-place="right"
      >
        <Typography variant="button1">Aerial Map</Typography>
        <FontAwesomeIcon className="pl-4" icon={faCircleInfo} />
      </div>

      <Typography variant="button2">Orientation</Typography>

      <div className="flex flex-row items-center justify-between pt-4">
        <OrientationOption
          icon={<FileHorizontal />}
          selected={selectedOrientation === Orientation.LANDSCAPE}
          onClick={() => setSelectedOrientation(Orientation.LANDSCAPE)}
        />
        <OrientationOption
          icon={<FileVertical />}
          selected={selectedOrientation === Orientation.PORTRAIT}
          onClick={() => setSelectedOrientation(Orientation.PORTRAIT)}
        />
      </div>

      <Button
        className="mt-4 h-8 w-full"
        isLoading={isLoading}
        variant={ButtonVariant.PRIMARY}
        onClick={captureImageFromBounds}
      >
        <Typography variant="button2">Take Screenshot</Typography>
      </Button>
      <Button
        className="mt-4 h-8 w-full text-gray-70"
        variant={ButtonVariant.GHOST}
        onClick={closeAerialMap}
      >
        <Typography variant="button2">Cancel</Typography>
      </Button>
    </div>
  );
};
